import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Snackbar,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
// New imports
import UndoIcon from "@mui/icons-material/Undo";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import StartIcon from "@mui/icons-material/PlayCircleOutline";
import StreakCounter from "../../../components/StreakCounter";
import { formatDateForStorage } from "../../../components/GameStateUtil";
import Grid from "@mui/material/Grid2";
import EndGameModal from "./EndGameModal";
import { Howl } from "howler";
import HowToPlayModal from "./HowToPlayModal";
import CalendarArchive from "../components/CalendarArchive";
import StarIcon from "@mui/icons-material/Star";
import {
  getPonderStreakFromStorage,
  updatePonderStreak,
  getIsPonderStreakActive,
} from "../components/PonderStreakUtil";
// Colorblind-friendly icons
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory"; // Triangle
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"; // Circle
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

const GRID_SIZE = 10;
// Define colors using ONLY the exact Wordy brand colors specified, but with increased contrast
const COLORS = [
  {
    id: "olive",
    color: "#b8c26c", // olive green
    borderColor: "#8a9340", // darker border for contrast
    icon: <CropSquareIcon />,
  },
  {
    id: "amber",
    color: "#ECB061", // amber
    borderColor: "#c48a3a", // darker border for contrast
    icon: <ChangeHistoryIcon />,
  },
  {
    id: "blue",
    color: "#91B2D1", // light blue
    borderColor: "#6a8eae", // darker border for contrast
    icon: <RadioButtonUncheckedIcon />,
  },
  {
    id: "lavender",
    color: "#c9bbde", // lavender
    borderColor: "#a394bb", // darker border for contrast
    icon: <StarBorderIcon />,
  },
  {
    id: "teal",
    color: "#5BBFBA", // teal (replacing tan for better differentiation)
    borderColor: "#3a9a96", // darker border for contrast
    icon: <FavoriteBorderIcon />,
  },
];

// Number of maximum undos allowed
const MAX_UNDOS = 3;

// Background color matching the app's cream/beige theme
const BACKGROUND_COLOR = "#8FBDC4"; // Cream background from screenshot
const ACCENT_COLOR = "#b8c26c"; // Olive green from brand colors
const SECONDARY_ACCENT = "#ECB061"; // Amber as secondary accent

const ColorFlood = () => {
  const theme = useTheme();
  const [grid, setGrid] = useState([]);
  const [currentColor, setCurrentColor] = useState("");
  const [moves, setMoves] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [win, setWin] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const hasInitialized = useRef(false);
  const [undosLeft, setUndosLeft] = useState(MAX_UNDOS);
  const [previousGrids, setPreviousGrids] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [floodedPercentage, setFloodedPercentage] = useState(0);
  const [streak, setStreak] = useState(0);
  const [isStreakActive, setIsStreakActive] = useState(false);
  const [isColorblindMode, setIsColorblindMode] = useState(false);

  // Additional state for GameControls
  const [showHowToModal, setShowHowToModal] = useState(false);

  // Tutorial refs
  const startingTileRef = useRef(null);
  const colorButtonsRef = useRef([]);

  const [bestMoves, setBestMoves] = useState(null);
  // Add new state for optimal solution
  const [optimalSolution, setOptimalSolution] = useState(null);
  const [isCalculatingOptimal, setIsCalculatingOptimal] = useState(false);
  // Add new state for new best score
  const [newBestScore, setNewBestScore] = useState(false);

  // Add new state for the onboarding modal
  const [showStartTutorial, setShowStartTutorial] = useState(true);

  // Add back showConfetti state for confetti effect
  const [showConfetti, setShowConfetti] = useState(false);
  const [showWinEffect, setShowWinEffect] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [isWinning, setIsWinning] = useState(false);

  // Add sound control state
  const [isMuted, setIsMuted] = useState(false);

  // Add click sound instance
  const clickSound = useRef(
    new Howl({
      src: ["/assets/audio/click.ogg", "/assets/audio/click.mp3"],
      volume: 0.5,
    })
  );
  // Add error sound instance
  const errorSound = useRef(
    new Howl({
      src: ["/assets/audio/error.ogg", "/assets/audio/error.mp3"],
      volume: 0.2,
    })
  );
  // Add win sound instance
  const winSound = useRef(
    new Howl({
      src: ["/assets/audio/level-win.mp3"],
      volume: 0.3,
    })
  );
  // Add button click sound instance
  const buttonClickSound = useRef(
    new Howl({
      src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
      volume: 0.2,
    })
  );

  // Create Web Worker for optimal solution calculation
  const workerRef = useRef(null);
  const workerTimeoutRef = useRef(null);

  // Add effect to handle mute state
  useEffect(() => {
    clickSound.current.mute(isMuted);
    errorSound.current.mute(isMuted);
    winSound.current.mute(isMuted);
    buttonClickSound.current.mute(isMuted);
  }, [isMuted]);

  useEffect(() => {
    // Only initialize if not already initialized or if the date changes
    if (!hasInitialized.current || !gameOver) {
      initializeFixedPuzzle();
      hasInitialized.current = true;
    }
  }, [currentDate]); // Re-run when date changes

  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const sessionTime = (Date.now() - startTime) / 1000; // in seconds
    };
  }, []);

  // Calculate flooded percentage whenever the grid or current color changes
  useEffect(() => {
    if (grid.length > 0) {
      calculateFloodedPercentage();
    }
  }, [grid, currentColor]);

  // Function to calculate the percentage of the board that has been flooded
  const calculateFloodedPercentage = () => {
    if (!grid.length) return;

    const totalCells = GRID_SIZE * GRID_SIZE;
    const floodedCells = grid
      .flat()
      .filter((cell) => cell === currentColor).length;
    const percentage = Math.round((floodedCells / totalCells) * 100);
    setFloodedPercentage(percentage);
  };

  // Add effect to load optimal solution when date changes
  useEffect(() => {
    const dateKey = formatDateForStorage(currentDate);
    const storedOptimal = localStorage.getItem(`colorflood_optimal-${dateKey}`);
    if (storedOptimal) {
      setOptimalSolution(parseInt(storedOptimal, 10));
      setIsCalculatingOptimal(false);
    }
  }, [currentDate]);

  // Add effect to load streak when date changes
  useEffect(() => {
    const { count } = getPonderStreakFromStorage("colorflood");
    setStreak(count);
    setIsStreakActive(getIsPonderStreakActive("colorflood"));
  }, [currentDate]);

  // Method for handling how-to modal
  const toggleHowToModal = () => setShowHowToModal((prev) => !prev);

  // Method for handling hint toggle
  const toggleHintModal = () => setShowHint((prev) => !prev);

  // Helper: Get all contiguous flooded region tiles (starting from [0,0])
  const getFloodedRegion = () => {
    if (!grid || grid.length === 0) return [];
    const visited = Array(GRID_SIZE)
      .fill(null)
      .map(() => Array(GRID_SIZE).fill(false));
    const region = [];
    const color = currentColor;
    const stack = [[0, 0]];
    while (stack.length > 0) {
      const [i, j] = stack.pop();
      if (
        i < 0 ||
        i >= GRID_SIZE ||
        j < 0 ||
        j >= GRID_SIZE ||
        visited[i][j] ||
        grid[i][j] !== color
      ) {
        continue;
      }
      visited[i][j] = true;
      region.push([i, j]);
      stack.push([i + 1, j], [i - 1, j], [i, j + 1], [i, j - 1]);
    }
    return region;
  };

  // Helper: is a tile (i, j) adjacent to the contiguous flooded region?
  const isTileAdjacentToFlooded = (i, j) => {
    if (!grid || grid.length === 0) return false;
    if (grid[i][j] === currentColor) return false;
    const floodedRegion = getFloodedRegion();
    for (const [fi, fj] of floodedRegion) {
      const directions = [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ];
      for (const [dx, dy] of directions) {
        const ni = fi + dx;
        const nj = fj + dy;
        if (ni === i && nj === j) {
          return true;
        }
      }
    }
    return false;
  };

  // Modify the floodFill function to handle invalid moves
  const floodFill = (colorId) => {
    if (gameOver || isAnimating) return;

    // Save current grid for undo
    setPreviousGrids([...previousGrids, JSON.parse(JSON.stringify(grid))]);

    const newGrid = [...grid];
    const stack = [[0, 0]];
    const originalColor = newGrid[0][0];
    const previousFloodedCount = countFloodedCells(newGrid, currentColor);

    // Set animating state
    setIsAnimating(true);

    while (stack.length > 0) {
      const [x, y] = stack.pop();
      if (
        x < 0 ||
        x >= GRID_SIZE ||
        y < 0 ||
        y >= GRID_SIZE ||
        newGrid[x][y] !== originalColor
      )
        continue;

      newGrid[x][y] = colorId;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }

    setGrid(newGrid);
    setCurrentColor(colorId);
    setMoves(moves + 1);

    const newMoves = moves + 1;

    // Calculate flooded percentage for animation
    const newFloodedCount = countFloodedCells(newGrid, colorId);
    const floodPercentage = (newFloodedCount / (GRID_SIZE * GRID_SIZE)) * 100;
    setFloodedPercentage(Math.round(floodPercentage));

    // End animation state after a short delay
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);

    // Check if game is won
    if (newGrid.every((row) => row.every((cell) => cell === colorId))) {
      setWin(true);
      setGameOver(true);
      setIsWinning(true);
      winSound.current.play();

      // Update streak
      const newStreak = updatePonderStreak(new Date(), "colorflood");
      setStreak(newStreak);
      setIsStreakActive(true);

      // Check for new best score BEFORE updating bestMoves
      const dateKey = formatDateForStorage(currentDate);
      const best = localStorage.getItem(`colorflood_bestMoves-${dateKey}`);
      if (!best || newMoves < parseInt(best, 10)) {
        setNewBestScore(true);
      } else {
        setNewBestScore(false);
      }

      // Store game completion data
      const gameData = {
        completed: true,
        moves: newMoves,
        goal: optimalSolution ? optimalSolution + 2 : null,
        best: best ? parseInt(best, 10) : newMoves,
      };
      localStorage.setItem(`colorflood-${dateKey}`, JSON.stringify(gameData));

      // Start the win animation sequence
      const totalTiles = GRID_SIZE * GRID_SIZE;
      const animationDuration = 2000; // 2 seconds total
      const interval = 40; // Update every 40ms for smoother animation
      const steps = animationDuration / interval;
      let currentStep = 0;

      // Create an array of all tile positions, ordered from top-left to bottom-right
      const tilePositions = [];
      for (let i = 0; i < GRID_SIZE; i++) {
        for (let j = 0; j < GRID_SIZE; j++) {
          // Calculate distance from top-left corner to determine flip order
          const distance = i + j;
          tilePositions.push([i, j, distance]);
        }
      }

      // Sort positions by distance from top-left
      const sortedPositions = tilePositions.sort((a, b) => a[2] - b[2]);

      const animateWin = () => {
        if (currentStep >= steps) {
          setIsWinning(false);
          setShowConfetti(true);
          setShowEndModal(true);
          return;
        }

        // Calculate how many tiles should be flipped at this step
        const progress = currentStep / steps;
        const tilesToFlip = Math.floor(progress * totalTiles);

        // Create a new grid with the winning color
        const animatedGrid = newGrid.map((row) => [...row]);

        // Flip the appropriate number of tiles in order
        for (let i = 0; i < tilesToFlip; i++) {
          const [x, y] = sortedPositions[i];
          animatedGrid[x][y] = colorId;
        }

        setGrid(animatedGrid);
        currentStep++;
        setTimeout(animateWin, interval);
      };

      // Start the animation
      animateWin();

      setSnackbarMessage(
        `Well done! You completed the puzzle in ${newMoves} moves.`
      );
      setShowSnackbar(true);
    }
  };

  // Helper function to count flood cells
  const countFloodedCells = (grid, colorId) => {
    return grid.reduce((count, row) => {
      return count + row.filter((cell) => cell === colorId).length;
    }, 0);
  };

  // Undo last move
  const undoMove = () => {
    if (undosLeft <= 0 || previousGrids.length === 0 || gameOver) return;

    // Vibration feedback for undo
    if (navigator.vibrate) {
      navigator.vibrate([20, 30, 20]);
    }

    const lastGrid = previousGrids.pop();
    setPreviousGrids([...previousGrids]);
    setGrid(lastGrid);
    setCurrentColor(lastGrid[0][0]);
    setMoves(Math.max(0, moves - 1));
    setUndosLeft(undosLeft - 1);
  };

  // Reset current puzzle
  const resetPuzzle = () => {
    // Vibration feedback for reset
    if (navigator.vibrate) {
      navigator.vibrate([10, 20, 10, 20, 10]);
    }

    initializeFixedPuzzle();
  };

  const getColorById = (id) => {
    return COLORS.find((color) => color.id === id);
  };

  const getHoverColor = (colorId) => {
    const color = getColorById(colorId).color;
    // More pronounced darkening for better contrast
    const darkenColor = (color, amount) => {
      return (
        "#" +
        color
          .replace(/^#/, "")
          .replace(/../g, (color) =>
            (
              "0" +
              Math.min(255, Math.max(0, parseInt(color, 16) - amount)).toString(
                16
              )
            ).substr(-2)
          )
      );
    };
    return darkenColor(color, 30); // Increased darkening amount
  };

  const handleShare = () => {
    const dateStr = currentDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const shareText = `Color Flood ${dateStr} - Completed in ${moves} moves!\n\nPlay at: ${window.location.href}`;

    // Use navigator.clipboard directly for better browser support
    navigator.clipboard
      .writeText(shareText)
      .then(() => {
        setSnackbarMessage("Score copied to clipboard!");
        setShowSnackbar(true);
      })
      .catch((error) => {
        console.error("Failed to copy text: ", error);
        setSnackbarMessage("Failed to copy score. Please try again.");
        setShowSnackbar(true);
      });
  };

  const changeDate = (increment) => {
    // Change the date and let the useEffect handle refreshing the grid
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + increment);
    setCurrentDate(newDate);

    // Force refresh of the grid
    hasInitialized.current = false;
  };

  // Get today's date with time set to midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Format the date as shown in the screenshot (e.g., "Mar 27, 2025")
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Get text color based on background color brightness
  const getTextColor = (backgroundColor) => {
    // Convert hex to RGB
    const r = parseInt(backgroundColor.slice(1, 3), 16);
    const g = parseInt(backgroundColor.slice(3, 5), 16);
    const b = parseInt(backgroundColor.slice(5, 7), 16);

    // Calculate brightness (YIQ formula)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Return black for bright colors, white for dark colors
    return brightness > 128 ? "#000000" : "#FFFFFF";
  };

  // Confetti cleanup
  useEffect(() => {
    if (showConfetti) {
      const timeout = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [showConfetti]);

  // Load best moves for the current date
  useEffect(() => {
    const dateKey = formatDateForStorage(currentDate);
    const best = localStorage.getItem(`colorflood_bestMoves-${dateKey}`);
    setBestMoves(best ? parseInt(best, 10) : null);
  }, [currentDate]);

  // Update best moves if user wins in fewer moves
  useEffect(() => {
    if (gameOver && win) {
      const dateKey = formatDateForStorage(currentDate);
      const best = localStorage.getItem(`colorflood_bestMoves-${dateKey}`);
      if (!best || moves < parseInt(best, 10)) {
        localStorage.setItem(
          `colorflood_bestMoves-${dateKey}`,
          moves.toString()
        );
        setBestMoves(moves);
      }
    }
  }, [gameOver, win, moves, currentDate]);

  // Add toggle mute function
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const [showCalendar, setShowCalendar] = useState(false);
  const [gameHistory, setGameHistory] = useState({});

  // Load game history from localStorage
  useEffect(() => {
    const loadGameHistory = () => {
      const history = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith("colorflood_bestMoves-")) {
          const date = key.replace("colorflood_bestMoves-", "");
          const moves = parseInt(localStorage.getItem(key), 10);
          history[date] = {
            moves,
            isBestScore: true,
          };
        }
      }
      setGameHistory(history);
    };

    loadGameHistory();
  }, []);

  // Update game history when a new best score is achieved
  useEffect(() => {
    if (gameOver && win && newBestScore) {
      const dateKey = formatDateForStorage(currentDate);
      setGameHistory((prev) => ({
        ...prev,
        [dateKey]: {
          moves,
          isBestScore: true,
        },
      }));
    }
  }, [gameOver, win, newBestScore, moves, currentDate]);

  const handleDateSelect = (date) => {
    setCurrentDate(date);
    setShowCalendar(false);
    // Reset game state
    setGrid([]);
    setCurrentColor("");
    setMoves(0);
    setGameOver(false);
    setWin(false);
    setShowSnackbar(false);
    setSnackbarMessage("");
    setShowHint(false);
    setUndosLeft(MAX_UNDOS);
    setPreviousGrids([]);
    setIsAnimating(false);
    setFloodedPercentage(0);
    setShowStartTutorial(true);
    setShowConfetti(false);
    setShowWinEffect(false);
    setShowEndModal(false);
    setIsWinning(false);
    // Initialize new puzzle
    initializeFixedPuzzle();
  };

  useEffect(() => {
    // Initialize Web Worker
    try {
      const workerUrl = new URL("./optimalSolutionWorker.js", import.meta.url);

      // Try creating worker without module type first
      let worker;
      try {
        worker = new Worker(workerUrl);
      } catch (moduleError) {
        worker = new Worker(workerUrl, { type: "module" });
      }

      workerRef.current = worker;

      // Handle messages from worker
      workerRef.current.onmessage = (e) => {
        // Clear timeout since we got a response
        if (workerTimeoutRef.current) {
          clearTimeout(workerTimeoutRef.current);
          workerTimeoutRef.current = null;
        }

        const { type, optimal, dateKey, error, progress } = e.data;

        switch (type) {
          case "result":
            setOptimalSolution(optimal);
            setIsCalculatingOptimal(false);
            localStorage.setItem(
              `colorflood_optimal-${dateKey}`,
              optimal.toString()
            );
            break;

          case "error":
            console.error("ColorFlood: Worker error:", error);
            setIsCalculatingOptimal(false);
            // Fallback to default optimal solution
            setOptimalSolution(16);
            break;

          case "progress":
            // Could add progress indicator here if needed
            break;

          default:
            // Handle legacy message format for backward compatibility
            if (optimal !== undefined && dateKey !== undefined) {
              setOptimalSolution(optimal);
              setIsCalculatingOptimal(false);
              localStorage.setItem(
                `colorflood_optimal-${dateKey}`,
                optimal.toString()
              );
            }
        }
      };

      // Handle worker errors
      workerRef.current.onerror = (error) => {
        console.error("ColorFlood: Worker error:", error);
        setIsCalculatingOptimal(false);
        // Fallback to default optimal solution
        setOptimalSolution(16);
      };
    } catch (error) {
      console.error("ColorFlood: Failed to initialize worker:", error);
      setIsCalculatingOptimal(false);
      // Fallback to default optimal solution
      setOptimalSolution(16);
    }

    return () => {
      // Cleanup worker on unmount
      if (workerRef.current) {
        workerRef.current.terminate();
      }

      // Clear any pending timeout
      if (workerTimeoutRef.current) {
        clearTimeout(workerTimeoutRef.current);
        workerTimeoutRef.current = null;
      }
    };
  }, []);

  // Modify initializeFixedPuzzle to handle worker errors
  const initializeFixedPuzzle = () => {
    // Use the date as seed for pseudo-randomness
    const dateString = `${currentDate.getFullYear()}${currentDate.getMonth()}${currentDate.getDate()}`;
    const seed = parseInt(dateString, 10);

    // Simple seeded random function
    const seededRandom = (seed, i = 0) => {
      const x = Math.sin(seed + i) * 10000;
      return x - Math.floor(x);
    };

    // Generate grid using the seeded random
    const newGrid = Array(GRID_SIZE)
      .fill()
      .map((_, rowIndex) =>
        Array(GRID_SIZE)
          .fill()
          .map((_, colIndex) => {
            const colorIndex = Math.floor(
              seededRandom(seed, rowIndex * GRID_SIZE + colIndex) *
                COLORS.length
            );
            return COLORS[colorIndex].id;
          })
      );

    setGrid(newGrid);
    setCurrentColor(newGrid[0][0]);
    setMoves(0);
    setGameOver(false);
    setWin(false);
    setUndosLeft(MAX_UNDOS);
    setPreviousGrids([]);

    // Check localStorage for optimal solution first
    const dateKey = formatDateForStorage(currentDate);
    const storedOptimal = localStorage.getItem(`colorflood_optimal-${dateKey}`);

    if (storedOptimal) {
      setOptimalSolution(parseInt(storedOptimal, 10));
      setIsCalculatingOptimal(false);
    } else if (workerRef.current) {
      // Only try to calculate if worker is available
      try {
        setIsCalculatingOptimal(true);

        // Create clean colors data without React elements
        const cleanColors = COLORS.map((color) => ({
          id: color.id,
          color: color.color,
          borderColor: color.borderColor,
        }));

        const message = {
          grid: newGrid,
          dateKey,
          gridSize: GRID_SIZE,
          colors: cleanColors,
        };
        workerRef.current.postMessage(message);

        // Set a timeout to handle unresponsive workers
        workerTimeoutRef.current = setTimeout(() => {
          console.warn(
            "ColorFlood: Worker timeout - no response received within 30 seconds"
          );
          setIsCalculatingOptimal(false);
          setOptimalSolution(16);
        }, 30000);
      } catch (error) {
        console.error("ColorFlood: Failed to send message to worker:", error);
        setIsCalculatingOptimal(false);
        // Fallback to default optimal solution
        setOptimalSolution(16);
      }
    } else {
      // If worker is not available, use default optimal solution
      setOptimalSolution(16);
    }
  };

  return (
    <Box sx={{ height: "100vh", backgroundColor: "#F5F0EB" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
          maxHeight: "655px",
          width: "100%",
          px: { xs: 1, sm: 2 },
          pt: { xs: 1, sm: 2 },
          pb: { xs: 1, sm: 2 },
          mx: "auto",
          gap: { xs: 0.5, sm: 1 },
          position: "relative", // For absolute positioning of tutorial elements
          backgroundColor: "#F5F0EB", // Foreground color
          borderRadius: 0,
          boxShadow: "none",
          overflow: "hidden", // Prevent content from overflowing
        }}
      >
        {/* Game HUD */}
        <Paper
          elevation={2}
          sx={{
            width: "100%",
            maxWidth: "400px",
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: "white",
          }}
        >
          {/* Top row: Controls, Date, Streak */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            {/* Left: Help and Sound controls */}
            <Box sx={{ display: "flex" }}>
              <Tooltip title="How to Play">
                <IconButton
                  onClick={() => {
                    buttonClickSound.current.play();
                    toggleHowToModal();
                  }}
                  sx={{
                    color: "#222",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                  }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                <IconButton
                  onClick={() => {
                    buttonClickSound.current.play();
                    toggleMute();
                  }}
                  sx={{
                    color: "#222",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                  }}
                >
                  {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Accessibility Mode">
                <IconButton
                  onClick={() => {
                    buttonClickSound.current.play();
                    setIsColorblindMode((prev) => !prev);
                  }}
                  sx={{
                    color: isColorblindMode
                      ? theme.palette.primary.main
                      : "#222",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                  }}
                >
                  <AccessibilityNewIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Center: Date */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "#222",
                cursor: "pointer",
                textDecoration: "underline",
                transition: "text-decoration 0.2s",
                "&:hover": { textDecoration: "underline dotted" },
                textAlign: "center",
              }}
              onClick={() => {
                buttonClickSound.current.play();
                setShowCalendar(true);
              }}
            >
              {currentDate.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>

            {/* Right: Streak */}
            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              <Tooltip title="Keep playing daily to unlock special color palettes!">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StreakCounter isActive={isStreakActive} streak={streak} />
                </Box>
              </Tooltip>
            </Box>
          </Box>

          {/* Bottom row: Moves and Best Score */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left: Moves */}
            <Chip
              label={`Moves: ${moves}`}
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.1)",
                "& .MuiChip-label": {
                  fontWeight: 600,
                  color: "#000000",
                },
              }}
            />

            {/* Center: Goal (dummy data) */}
            <Chip
              icon={<StarIcon sx={{ color: "#FFD700" }} />} // Gold star
              label={
                isCalculatingOptimal
                  ? "Calculating..."
                  : `Goal: ${
                      optimalSolution !== null ? optimalSolution + 2 : "---"
                    }`
              }
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.1)",
                mx: 1,
                "& .MuiChip-label": {
                  fontWeight: 600,
                  color: "#222",
                },
              }}
            />

            {/* Right: Best Score */}
            <Chip
              icon={<EmojiEventsIcon sx={{ color: "#FFD700" }} />}
              label={`Best: ${bestMoves !== null ? bestMoves : "---"}`}
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.1)",
                "& .MuiChip-label": {
                  fontWeight: 600,
                  color: "#222",
                },
                animation: newBestScore ? "pulse 1s ease-in-out" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.05)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
          </Box>
        </Paper>

        {/* Confetti effect for wins */}
        {showConfetti && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={500}
            gravity={0.15}
            colors={COLORS.map((c) => c.color)}
          />
        )}

        {/* Game Board - Optimized for mobile with tutorial highlight for first tile */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "400px",
            aspectRatio: "1/1",
            flex: "1 1 auto",
            bgcolor: "#F5F0EB",
            mb: 0,
            boxSizing: "border-box",
            position: "relative",
            transition: "all 0.3s ease",
            ...(isWinning && {
              transform: "scale(1.02)",
              boxShadow: "0 0 30px rgba(255,255,255,0.8)",
            }),
          }}
        >
          <Grid
            container
            spacing={0.25}
            sx={{
              p: 0.25,
              height: "100%",
              m: 0,
              width: "100%",
              perspective: "1000px",
              transformStyle: "preserve-3d",
            }}
          >
            {grid.map((row, i) =>
              row.map((cell, j) => {
                const colorInfo = getColorById(cell);
                const isStartingTile = i === 0 && j === 0;
                return (
                  <Grid
                    item
                    size={1.2}
                    key={`${i}-${j}`}
                    sx={{
                      p: "1px",
                      boxSizing: "border-box",
                    }}
                    ref={isStartingTile ? startingTileRef : null}
                  >
                    <Box
                      component={motion.div}
                      animate={{
                        scale:
                          isAnimating && cell === currentColor
                            ? [1, 1.25, 0.9, 1.15, 1]
                            : isWinning
                            ? [1, 1.1, 0.9, 1.05, 1]
                            : 1,
                        rotateX: isWinning ? [0, 180, 0] : 0,
                        transition: {
                          duration: isWinning ? 0.4 : 0.8,
                          times: isWinning
                            ? [0, 0.5, 1]
                            : [0, 0.2, 0.4, 0.6, 1],
                          ease: isWinning ? "easeInOut" : [0.34, 1.56, 0.64, 1],
                          delay: isWinning ? (i + j) * 0.05 : 0,
                        },
                      }}
                      whileHover={{
                        scale: 1.05,
                        transition: {
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        },
                      }}
                      sx={{
                        width: "100%",
                        paddingBottom: "100%",
                        backgroundColor: colorInfo ? colorInfo.color : "#fff",
                        border:
                          showStartTutorial && isStartingTile
                            ? "1px solid #615B5B"
                            : colorInfo
                            ? `1px solid ${colorInfo.borderColor}`
                            : "1px solid #ccc",
                        transition: "all 0.3s",
                        position: "relative",
                        zIndex: isStartingTile && showStartTutorial ? 2 : 1,
                        transformOrigin: "center",
                        willChange: "transform",
                        perspective: "1000px",
                        transformStyle: "preserve-3d",
                        backfaceVisibility: "hidden",
                        ...(isWinning && {
                          boxShadow: "0 0 10px rgba(255,255,255,0.5)",
                        }),
                      }}
                      onClick={() => {
                        if (gameOver || isAnimating) return;
                        if (cell === currentColor) return;
                        if (!isTileAdjacentToFlooded(i, j)) return;
                        clickSound.current.play();
                        floodFill(cell);
                        if (showStartTutorial) setShowStartTutorial(false);
                      }}
                    >
                      {isColorblindMode && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: getTextColor(colorInfo.color),
                            pointerEvents: "none",
                            fontSize: "1.6rem",
                          }}
                        >
                          {React.cloneElement(colorInfo.icon, {
                            sx: { fontSize: "inherit" },
                          })}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Box>
        {/* Color Selection with Tutorial Reference */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: { xs: 1, sm: 1.5 },
            flexWrap: "nowrap",
            maxWidth: "400px",
            width: "100%",
            flexShrink: 0,
            mt: { xs: 2, sm: 3 },
            mb: { xs: 0.5, sm: 1 },
            pt: 0,
            position: "relative",
          }}
        >
          {COLORS.map((colorInfo, index) => (
            <Button
              key={colorInfo.id}
              onClick={() => {
                if (gameOver || isAnimating) return;
                if (colorInfo.id === currentColor) {
                  errorSound.current.play();
                } else {
                  clickSound.current.play();
                  floodFill(colorInfo.id);
                  if (showStartTutorial) setShowStartTutorial(false);
                }
              }}
              disabled={gameOver || isAnimating}
              component={motion.div}
              whileTap={!(gameOver || isAnimating) ? { scale: 0.92 } : {}}
              animate={
                showStartTutorial && colorInfo.id !== currentColor
                  ? {
                      scale: [1, 1.07, 1],
                      boxShadow: [
                        "0 1px 3px rgba(0,0,0,0.15)",
                        "0 3px 8px rgba(0,0,0,0.25)",
                        "0 1px 3px rgba(0,0,0,0.15)",
                      ],
                    }
                  : {}
              }
              transition={{
                repeat:
                  showStartTutorial && colorInfo.id !== currentColor
                    ? Infinity
                    : 0,
                duration: 1.5,
              }}
              sx={{
                width: { xs: 45, sm: 55 },
                height: { xs: 45, sm: 55 },
                minWidth: { xs: 45, sm: 55 },
                p: 0,
                borderRadius: "5px",
                backgroundColor: colorInfo.color,
                border:
                  colorInfo.id === currentColor
                    ? "2px solid white"
                    : `1px solid ${colorInfo.borderColor}`,
                boxShadow:
                  colorInfo.id === currentColor
                    ? `0 0 6px 1px ${colorInfo.color}`
                    : "0 1px 2px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
                "&:active": {
                  backgroundColor: colorInfo.color,
                  opacity: 0.85,
                  transform: "scale(0.95)",
                },
                "&:disabled": {
                  backgroundColor: colorInfo.color,
                  opacity: colorInfo.id === currentColor ? 1 : 0.6,
                },
                flex: 1,
                maxWidth: { xs: 50, sm: 60 },
              }}
              aria-label={`Select ${colorInfo.id} color`}
            >
              {isColorblindMode && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: getTextColor(colorInfo.color),
                    pointerEvents: "none",
                    fontSize: "1.8rem",
                  }}
                >
                  {React.cloneElement(colorInfo.icon, {
                    sx: { fontSize: "inherit" },
                  })}
                </Box>
              )}
            </Button>
          ))}
        </Box>
        {/* Floating modal above the color selection area */}
        {showStartTutorial && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: { xs: 90, sm: 110 },
              transform: "translateX(-50%)",
              zIndex: 200,
              bgcolor: "white",
              p: 0,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
              border: "2px solid #b8c26c",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: { xs: "1.1rem", sm: "1.2rem" },
              minWidth: 275,
              textAlign: "center",
              pointerEvents: "auto",
              cursor: "pointer",
              transition: "box-shadow 0.2s, border 0.2s",
              "&:hover": {
                boxShadow: "0 6px 24px rgba(184,194,108,0.25)",
                border: "2px solid #ECB061",
              },
            }}
            onClick={() => {
              buttonClickSound.current.play();
              setShowStartTutorial(false);
              setShowHowToModal(true);
            }}
            role="button"
            tabIndex={0}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1.2,
                px: 2.5,
              }}
            >
              Show me how to play
              <ChevronRightIcon sx={{ fontSize: 28, ml: 0.5 }} />
            </Box>
          </Box>
        )}
        {/* How To Play Modal */}
        <HowToPlayModal open={showHowToModal} onClose={toggleHowToModal} />
        {/* Game Complete Message - More compact for mobile */}
        <EndGameModal
          open={showEndModal}
          onClose={() => {
            buttonClickSound.current.play();
            setGameOver(false);
            setNewBestScore(false);
            setShowWinEffect(false);
            setShowEndModal(false);
            setIsWinning(false);
          }}
          moves={moves}
          bestMoves={bestMoves !== null ? bestMoves : moves}
          newBestScore={newBestScore}
          onPlayAgain={() => {
            buttonClickSound.current.play();
            initializeFixedPuzzle();
            setGameOver(false);
            setNewBestScore(false);
            setShowWinEffect(false);
            setShowEndModal(false);
            setIsWinning(false);
          }}
        />
        {/* Calendar Archive Modal */}
        <CalendarArchive
          open={showCalendar}
          onClose={() => setShowCalendar(false)}
          gameKey="colorflood"
          getGameDataForDate={(dateKey) => {
            const data = localStorage.getItem(`colorflood-${dateKey}`);
            if (!data) {
              return null;
            }
            const gameData = JSON.parse(data);
            return {
              completed: gameData.completed,
              goalMet: gameData.moves <= gameData.goal,
              moves: gameData.moves,
              best: gameData.best ? `${gameData.best} moves` : null,
            };
          }}
          formatDateForStorage={formatDateForStorage}
          title="ColorFlood Archive"
          description="View your past ColorFlood games and achievements"
          onDateSelect={handleDateSelect}
          currentDate={currentDate}
        />
      </Box>
    </Box>
  );
};

export default ColorFlood;
